import { Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Pipe({
  name: 'relativeDateFormatter',
  pure: false,
})
export class RelativeDateFormatterPipe implements PipeTransform {
  constructor(private translateService: TranslateService) {}

  transform(d: Date, ...args: any[]): string {
    if (!d) {
      return '';
    }

    const capitalizeFirstLetter = args?.[0];
    const roundValueToFloor = args?.[1];

    const secondSingular = this.translateService.instant('pipes.time.second');
    const secondPlural = this.translateService.instant('pipes.time.seconds');
    const minuteSingular = this.translateService.instant('pipes.time.minute');
    const minutePlural = this.translateService.instant('pipes.time.minutes');
    const hourSingular = this.translateService.instant('pipes.time.hour');
    const hourPlural = this.translateService.instant('pipes.time.hours');
    const daySingular = this.translateService.instant('pipes.time.day');
    const dayPlural = this.translateService.instant('pipes.time.days');
    const monthSingular = this.translateService.instant('pipes.time.month');
    const monthPlural = this.translateService.instant('pipes.time.months');
    const yearSingular = this.translateService.instant('pipes.time.year');
    const yearPlural = this.translateService.instant('pipes.time.years');

    const now = Date.now();
    const isInFuture = now < d.getTime();

    const diff = Math.abs(now - d.getTime());
    const deltaSecond = roundValueToFloor
      ? Math.floor(diff / 1000)
      : Math.ceil(diff / 1000);
    const deltaMinute = roundValueToFloor
      ? Math.floor(diff / (60 * 1000))
      : Math.ceil(diff / (60 * 1000));
    const deltaHour = Math.floor(diff / (60 * 60 * 1000));
    const deltaDay = Math.floor(diff / (24 * 60 * 60 * 1000));
    const deltaMonth = Math.floor(diff / (30 * 24 * 60 * 60 * 1000));
    const deltaYear = Math.floor(diff / (365 * 24 * 60 * 60 * 1000));

    let difAsStr = null;
    if (deltaYear > 1) {
      difAsStr = `${deltaYear} ${yearPlural}`;
    } else if (deltaYear > 0) {
      difAsStr = `${deltaYear} ${yearSingular}`;
    } else if (deltaMonth > 1) {
      difAsStr = `${deltaMonth} ${monthPlural}`;
    } else if (deltaMonth > 0) {
      difAsStr = `${deltaMonth} ${monthSingular}`;
    } else if (deltaDay > 1) {
      difAsStr = `${deltaDay} ${dayPlural}`;
    } else if (deltaDay > 0) {
      difAsStr = `${deltaDay} ${daySingular}`;
    } else if (deltaHour > 1) {
      difAsStr = `${deltaHour} ${hourPlural}`;
    } else if (deltaHour > 0) {
      difAsStr = `${deltaHour} ${hourSingular}`;
    } else if (deltaMinute > 1) {
      difAsStr = `${deltaMinute} ${minutePlural}`;
    } else if (deltaMinute > 0) {
      difAsStr = `${deltaMinute} ${minuteSingular}`;
    } else if (deltaSecond > 1) {
      difAsStr = `${deltaSecond} ${secondPlural}`;
    } else if (deltaSecond > 0) {
      difAsStr = `${deltaSecond} ${secondSingular}`;
    }

    if (difAsStr) {
      const relativeTimeStr = this.translateService.instant(
        isInFuture
          ? 'pipes.time.timestamp_in_future'
          : 'pipes.time.timestamp_in_the_past',
        { time: difAsStr }
      );
      return capitalizeFirstLetter
        ? this.capitalizeFirstLetter(relativeTimeStr)
        : relativeTimeStr;
    } else {
      const nowStr = this.translateService.instant('pipes.time.now');
      return capitalizeFirstLetter
        ? this.capitalizeFirstLetter(nowStr)
        : nowStr;
    }
  }

  private capitalizeFirstLetter(text: string): string {
    return text.charAt(0).toUpperCase() + text.slice(1);
  }
}
